// filepath: apps/web/src/features/admin/fieldVisibility/RoleFieldVisibilityTable.tsx
//
// FR-UI-ADM-4 — Pure render component for the admin viewer's
// role × entity-type field-classification table.
//
// Iterates the server-returned `fields` map (the canonical
// server-resolved set) and renders one row per field. Each row
// uses FieldVisibilityStateCell (the single switch for the three
// states). NO collapse — every field appears as its own row
// (AC-3 / AC-7).
//
// INV-9: no dangerouslySetInnerHTML. Server strings render as JSX
// text children.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type {
  FieldVisibilityState,
  RoleFieldVisibilityResponse,
} from "./types";
import { FieldVisibilityStateCell } from "./FieldVisibilityStateCell";
import type { Locale } from "@/i18n/useTranslation";

interface RoleFieldVisibilityTableProps {
  /** The server-resolved response — fields map keys are the field ids to render. */
  data: RoleFieldVisibilityResponse;
  /** Active locale — passed down to FieldVisibilityStateCell for i18n-aware labels. */
  locale: Locale;
}

/**
 * RoleFieldVisibilityTable — pure render. Renders one
 * `<tr data-state="…">` per field; uses FieldVisibilityStateCell
 * for the cell content.
 *
 * Field ordering: insertion order of the server-returned
 * `fields` map (Object.keys preserves insertion order in v8).
 * The server emits fields in the order the entity-type
 * definition declares them (entity.EntityDefinition.Fields
 * iteration).
 */
export function RoleFieldVisibilityTable({
  data,
  locale,
}: RoleFieldVisibilityTableProps): React.ReactElement {
  const { t } = useTranslation();

  // Object.keys() preserves insertion order in modern JS engines.
  const fieldIds = Object.keys(data.fields);

  return (
    <div
      className="space-y-2"
      data-testid="role-field-visibility-table"
      aria-label={t(
        "admin.field_visibility.aria.table_label",
        "Field visibility",
        {
          roleName: data.role,
          entityType: data.entity_type,
        },
      )}
    >
      <table
        className="min-w-full border-collapse text-sm"
        aria-label={t(
          "admin.field_visibility.aria.table_label",
          "Field visibility",
          {
            roleName: data.role,
            entityType: data.entity_type,
          },
        )}
      >
        <thead>
          <tr className="border-b border-border">
            <th className="px-3 py-2 text-left font-medium" scope="col">
              {t("admin.field_visibility.column.field", "Field")}
            </th>
            <th
              className="px-3 py-2 text-left font-medium"
              scope="col"
              data-testid="column-classification"
            >
              {t(
                "admin.field_visibility.column.classification",
                "Classification",
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {fieldIds.map((fieldId) => {
            // The server may emit an undefined value if a field is
            // in a transition state. Coerce to "visible" so the
            // rendered row carries a defined data-state — the
            // server is the source of truth for the three-state
            // vocabulary; an undefined value here is a server
            // regression, not a client-side fallback. Tests assert
            // the contract via the unit suite.
            const classification: FieldVisibilityState =
              data.fields[fieldId] ?? "visible";
            return (
              <tr
                key={fieldId}
                className="border-b border-border"
                data-testid={`row-${fieldId}`}
                data-field-id={fieldId}
                data-classification={classification}
              >
                <th
                  scope="row"
                  className="px-3 py-2 text-left font-medium"
                  data-testid={`cell-field-${fieldId}`}
                >
                  {fieldId}
                </th>
                <td
                  className="px-3 py-2"
                  data-testid={`cell-classification-${fieldId}`}
                >
                  <FieldVisibilityStateCell
                    fieldId={fieldId}
                    classification={classification}
                    locale={locale}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {fieldIds.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="empty-state">
          {t("admin.field_visibility.empty", "No fields to display.")}
        </p>
      )}
    </div>
  );
}
